import { Component } from '@theme/component';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const KLAVIYO_BACK_IN_STOCK_URL = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_API_REVISION = '2026-07-15';

export class NotifyMeComponent extends Component {
  requiredRefs = ['notifyMeTrigger', 'notifyMeFormWrapper', 'notifyMeConfirmed'];

  showForm() {
    this.refs.notifyMeTrigger.setAttribute('hidden', 'true');
    this.refs.notifyMeFormWrapper.removeAttribute('hidden');
    this.refs.notifyMeEmailInput?.focus({ preventScroll: true });
  }

  clearError() {
    this.refs.notifyMeError?.setAttribute('hidden', '');
    this.refs.notifyMeSubmitError?.setAttribute('hidden', '');
    this.refs.notifyMeEmailInput?.removeAttribute('aria-invalid');
    this.refs.notifyMeEmailInput?.removeAttribute('aria-describedby');
  }

  #describeInputBy(message) {
    const input = this.refs.notifyMeEmailInput;
    if (input && message?.id) input.setAttribute('aria-describedby', message.id);
  }

  handleEmailKeydown(event) {
    if (event.key !== 'Enter' || event.isComposing) return;
    this.handleSubmit(event);
  }

  async handleSubmit(event) {
    event.preventDefault();

    const input = this.refs.notifyMeEmailInput;
    if (!(input instanceof HTMLInputElement)) return;

    this.clearError();
    const email = input.value.trim();
    input.value = email;

    if (!EMAIL_PATTERN.test(email) || !input.checkValidity()) {
      input.setAttribute('aria-invalid', 'true');
      this.refs.notifyMeError?.removeAttribute('hidden');
      this.#describeInputBy(this.refs.notifyMeError);
      input.focus();
      return;
    }

    const submitButton = this.refs.notifyMeSubmit;
    submitButton?.setAttribute('disabled', 'true');

    try {
      await this.subscribeToKlaviyo(email);
      this.refs.notifyMeFormWrapper?.setAttribute('hidden', '');
      this.refs.notifyMeConfirmed?.removeAttribute('hidden');
      this.refs.notifyMeConfirmed?.focus();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      this.refs.notifyMeSubmitError?.removeAttribute('hidden');
      this.#describeInputBy(this.refs.notifyMeSubmitError);
      input.focus();
    } finally {
      submitButton?.removeAttribute('disabled');
    }
  }

  async subscribeToKlaviyo(email) {
    const { klaviyoApiKey, variantId } = this.dataset;

    if (!klaviyoApiKey) {
      throw new Error('Notify me: missing Klaviyo public API key');
    }

    const response = await fetch(`${KLAVIYO_BACK_IN_STOCK_URL}?company_id=${encodeURIComponent(klaviyoApiKey)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_API_REVISION,
      },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: { email },
              },
            },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`,
              },
            },
          },
        },
      }),
    });

    if (!response.ok && response.status !== 202) {
      throw new Error(`Notify me: Klaviyo request failed with status ${response.status}`);
    }
  }
}

if (!customElements.get('notify-me-component')) {
  customElements.define('notify-me-component', NotifyMeComponent);
}

document.addEventListener('click', (event) => {
  const opener = event.target instanceof Element ? event.target.closest('[data-notify-me-open]') : null;
  if (!opener) return;

  const notifyMe = [...document.querySelectorAll('main notify-me-component')].find(
    (element) => element instanceof NotifyMeComponent && !element.closest('[hidden]')
  );
  if (!(notifyMe instanceof NotifyMeComponent)) return;

  event.preventDefault();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  notifyMe.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });

  const confirmed = notifyMe.refs.notifyMeConfirmed;
  if (confirmed && !confirmed.hidden) {
    confirmed.focus({ preventScroll: true });
  } else {
    notifyMe.showForm();
  }
});
